import type { UIMatch } from 'react-router';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import type { AppDisplayMode } from '@archipro-design/aria';
import type { APHandle, HandleHideNavigation } from '@modules/root/type/handle';

export interface UseHideNavigationResult {
    topNavigation?: boolean;
    footerNavigation?: boolean;
}

interface HideNavigationHandleArgs {
    hideTopNavOn?: HandleHideNavigation;
    hideFooterOn?: HandleHideNavigation;
    displayMode?: AppDisplayMode;
}

export const hideNavigationHandle = (
    args: HideNavigationHandleArgs,
): UseHideNavigationResult => {
    const { hideTopNavOn, hideFooterOn, displayMode } = args;

    return {
        topNavigation:
            !displayMode || !hideTopNavOn ? true : !hideTopNavOn[displayMode],
        footerNavigation:
            !displayMode || !hideFooterOn ? true : !hideFooterOn[displayMode],
    };
};

/**
 * True when a matched route is closing its grid with the content gate.
 *
 * The footer goes with the sections under the gate: it is a full set of
 * category and vertical links, so leaving it standing under the wall is a way
 * around the wall. Read from loader data rather than a handle because whether
 * a page gates is decided per request, not per route.
 *
 * A crawler is never gated — `resolveContentGate` bails on `device.isBot`
 * before anything else — so the indexed page keeps its footer and its internal
 * links. Only a logged-out human loses it.
 */
const hasContentGate = (matches: UIMatch[]): boolean =>
    matches.some(
        (match) =>
            (match.data as { contentGate?: { gated?: boolean } } | null)
                ?.contentGate?.gated === true,
    );

export const useHideNavigation = (
    matches: UIMatch[],
): UseHideNavigationResult => {
    const routeError = useRouteError();
    const gated = hasContentGate(matches);

    const hideFooterOn = (
        matches.find(
            (match) =>
                match.handle && match.handle.hasOwnProperty('hideFooter'),
        ) as UIMatch<unknown, APHandle> | undefined
    )?.handle?.hideFooter;

    const hideTopNavOn = (
        matches.find(
            (match) =>
                match.handle &&
                match.handle.hasOwnProperty('hideTopNavigation'),
        ) as UIMatch<unknown, APHandle> | undefined
    )?.handle?.hideTopNavigation;

    const appDisplayMode = useAppDisplayModeContext();

    if (isRouteErrorResponse(routeError)) {
        if (routeError.status === 401) {
            return {
                topNavigation: false,
                footerNavigation: false,
            };
        }
        if (routeError.status === 404) {
            return {
                topNavigation: true,
                footerNavigation: true,
            };
        }
    }

    if (!hideFooterOn && !hideTopNavOn) {
        return {
            topNavigation: true,
            footerNavigation: !gated,
        };
    }

    const fromHandle = hideNavigationHandle({
        hideTopNavOn,
        hideFooterOn,
        displayMode: (Object.keys(appDisplayMode) as AppDisplayMode[]).find(
            (key) => appDisplayMode[key] === true,
        ) as AppDisplayMode,
    });

    return {
        ...fromHandle,
        footerNavigation: fromHandle.footerNavigation && !gated,
    };
};
